import { liveQuery } from 'dexie'
import type { QueryClient } from '@tanstack/react-query'

import {
  DRAFTS_QUERY_KEY,
  FIELD_APPOINTMENTS_QUERY_KEY,
  MEDIA_QUERY_KEY,
  OUTBOX_QUERY_KEY,
  PASSAGE_NOTICES_QUERY_KEY,
} from './query-keys'
import type { TeamDataStore } from './team-data'

/**
 * Le pont entre Dexie et TanStack Query (D21).
 *
 * La source de vérité est **IndexedDB, pas le réseau** : une lecture ne périme donc jamais
 * d'elle-même, c'est une écriture locale qui la rend obsolète. `liveQuery` observe ces
 * écritures — d'où qu'elles viennent, y compris de l'outbox qui repart toute seule en arrière-
 * plan — et invalide la clé concernée. C'est ce qui fait changer l'état de synchro **sous les
 * yeux de l'agent** sans qu'aucun écran ait à interroger quoi que ce soit.
 *
 * Les observations lisent la base de **l'équipe connectée** (D22 pt.4), et n'observent
 * rien tant qu'aucune n'est ouverte : ce point d'entrée démarre avant la lecture de la session
 * locale, et observer la base d'hier montrerait la file de l'équipe d'hier.
 *
 * ⚠️ **Un changement d'équipe remonte les observations**, et il le faut dans les deux
 * sens : une observation posée sur une base **fermée** ne se réveille plus jamais (le porteur
 * referme la sortante), et une base neuve n'a jamais été observée. Les remonter est aussi ce qui
 * fait relire l'écran après une reconnexion sur une autre équipe — un abonnement neuf émet sa
 * première valeur, donc invalide.
 *
 * Rend la fonction de désabonnement : sans elle, un observateur survivrait au démontage et
 * invaliderait le cache d'un client Query mort.
 */
export function watchLocalData(
  store: TeamDataStore,
  queryClient: QueryClient,
  options: WatchOptions = {},
): () => void {
  function watchCurrent(): (() => void)[] {
    const db = store.current()
    if (db === null) return []

    return [
      observe(queryClient, OUTBOX_QUERY_KEY, () => db.outbox.toArray(), options),
      observe(queryClient, DRAFTS_QUERY_KEY, () => db.drafts.toArray(), options),
      // Un refus tombe pendant une passe d'arrière-plan : la pastille doit le voir sans navigation.
      observe(queryClient, FIELD_APPOINTMENTS_QUERY_KEY, () => db.appointments.toArray(), options),
      // Un avis parti ou refusé change d'état pendant la passe : « Notifications » le montre (#655).
      observe(queryClient, PASSAGE_NOTICES_QUERY_KEY, () => db.passageNotices.toArray(), options),
      // Les médias : c'est cette clé qui rend visible la progression d'un envoi de plusieurs
      // vidéos, média par média, au lieu d'un écran figé jusqu'au dernier octet.
      //
      // ⚠️ Lire les **enregistrements**, pas les clés : ce qui avance ici est `uploadedAt`, qui
      // n'est indexé nulle part. Une observation des clés seules ne se réveille donc jamais sur
      // un média monté (vérifié) — elle ne verrait que les ajouts et les suppressions.
      // ⚠️ Contrepartie à surveiller sur tablette : chaque écriture relit toute la table. Le
      // `Blob` revient en référence, pas en octets, mais le coût croît avec le nombre de pièces.
      observe(queryClient, MEDIA_QUERY_KEY, () => db.media.toArray(), options),
    ]
  }

  function stopAll(): void {
    for (const stop of stops) stop()
  }

  let stops = watchCurrent()

  // Le porteur ne prévient que d'un **changement de base**, jamais d'un changement de session :
  // un rafraîchissement de jeton toutes les quinze minutes (M23) ne remonte rien.
  const unsubscribe = store.subscribe(() => {
    stopAll()
    stops = watchCurrent()
  })

  return () => {
    unsubscribe()
    stopAll()
  }
}

export interface WatchOptions {
  /** Délai avant de remettre une observation morte en place. */
  retryDelayMs?: number
  /** Nombre de remises en place consécutives avant d'abandonner. */
  maxRetries?: number
}

const DEFAULT_RETRY_DELAY_MS = 1_000
const DEFAULT_MAX_RETRIES = 3

/**
 * Une observation qui **survit à son propre échec**.
 *
 * Une souscription morte est le pire des cas : elle ne dit rien, et l'indicateur de synchro
 * reste sur « en attente » jusqu'au rechargement pendant que la file, elle, avance. Deux gestes
 * donc, dans cet ordre :
 *
 * 1. **invalider la clé** — l'écran relit Dexie et montre l'état réel, tout de suite, même si
 *    plus rien ne devait le pousser ensuite ;
 * 2. **remettre l'observation en place**, après un délai et un nombre de fois borné. Le délai
 *    n'est pas une précaution de style : sans lui, une base définitivement fermée ferait tourner
 *    la reprise en boucle serrée. Le compteur repart à zéro dès qu'une valeur passe à nouveau —
 *    une tournée dure des heures, et deux incidents espacés n'ont pas à figer l'écran.
 */
function observe(
  queryClient: QueryClient,
  queryKey: readonly unknown[],
  read: () => Promise<unknown>,
  options: WatchOptions,
): () => void {
  const retryDelayMs = options.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES

  let stopped = false
  let retries = 0
  let retryTimer: ReturnType<typeof setTimeout> | null = null

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey })
  }

  function subscribe(): { unsubscribe: () => void } {
    return liveQuery(read).subscribe({
      next: () => {
        retries = 0
        invalidate()
      },
      error: (error: unknown) => {
        console.error(`[sync] Observation locale interrompue sur « ${String(queryKey)} ».`, error)
        invalidate()

        if (stopped || retries >= maxRetries) return
        retries += 1
        retryTimer = setTimeout(() => {
          retryTimer = null
          if (!stopped) subscription = subscribe()
        }, retryDelayMs)
      },
    })
  }

  let subscription = subscribe()

  return () => {
    stopped = true
    if (retryTimer !== null) clearTimeout(retryTimer)
    subscription.unsubscribe()
  }
}
